import React, { useState, useEffect } from 'react';

interface Trigger {
  id: string;
  text: string;
}

interface Response {
  id: string;
  text: string;
}

interface Blindspot {
  id: string;
  description: string;
  selfJustification: string;
  canCrack: boolean;
}

interface Mutation {
  id: string;
  name: string;
  description: string;
  probability: number;
}

interface Enzyme {
  id: string;
  characterName: string;
  traitName: string;
  intensity: number;
  triggers: Trigger[];
  responses: Response[];
  blindspots: Blindspot[];
  mutations: Mutation[];
  createdAt: number;
}

interface AttachedEnzyme {
  enzymeId: string;
  intensity: number;
}

interface Character {
  id: string;
  name: string;
  description: string;
  attachedEnzymes: AttachedEnzyme[];
  createdAt: number;
}

interface TensionCodon {
  id: string;
  name: string;
  description: string;
  pressureLevel: number;
  affectedCharacterIds: string[];
  createdAt: number;
}

type EscalationType = 'slow-burn' | 'hair-trigger' | 'wave';

interface EscalationProfile {
  type: EscalationType;
  slowBurnReinforcements?: number;
  hairTriggerRecoveryTime?: number;
  waveBuildDuration?: number;
  waveReleaseIntensity?: number;
}

interface Encounter {
  id: string;
  title: string;
  description: string;
  characterIds: string[];
  triggers: string;
  whatHappens: string;
  playerChoicePoints: string[];
  createdAt: number;
}

interface ExportData {
  version: string;
  timestamp: string;
  enzymes: Enzyme[];
  characters: Character[];
  tensionCodons: TensionCodon[];
  escalationProfile: EscalationProfile;
}

function App() {
  const [characterName, setCharacterName] = useState('');
  const [traitName, setTraitName] = useState('');
  const [intensity, setIntensity] = useState(0.5);
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [responses, setResponses] = useState<Response[]>([]);
  const [blindspots, setBlindspots] = useState<Blindspot[]>([]);
  const [mutations, setMutations] = useState<Mutation[]>([]);
  const [newTrigger, setNewTrigger] = useState('');
  const [newResponse, setNewResponse] = useState('');
  const [buttonHover, setButtonHover] = useState<string | null>(null);
  const [removeHover, setRemoveHover] = useState<string | null>(null);
  const [savedEnzymes, setSavedEnzymes] = useState<Enzyme[]>([]);
  const [selectedEnzymeId, setSelectedEnzymeId] = useState<string | null>(null);
  const [enzymeHover, setEnzymeHover] = useState<string | null>(null);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [currentView, setCurrentView] = useState<'editor' | 'characters' | 'storyworld' | 'encounters'>('editor');
  const [newCharacterName, setNewCharacterName] = useState('');
  const [newCharacterDescription, setNewCharacterDescription] = useState('');
  const [characterHover, setCharacterHover] = useState<string | null>(null);
  const [selectedCharacterId, setSelectedCharacterId] = useState<string | null>(null);
  const [tensionCodons, setTensionCodons] = useState<TensionCodon[]>([]);
  const [tensionCodonHover, setTensionCodonHover] = useState<string | null>(null);
  const [escalationProfile, setEscalationProfile] = useState<EscalationProfile>({
    type: 'slow-burn',
    slowBurnReinforcements: 3,
    hairTriggerRecoveryTime: 1,
    waveBuildDuration: 5,
    waveReleaseIntensity: 0.7,
  });
  const [cobotPanelOpen, setCobotPanelOpen] = useState(false);
  const [cobotSuggestions, setCobotSuggestions] = useState<{ triggers: string[]; responses: string[] }>({ triggers: [], responses: [] });
  const [cobotLoading, setCobotLoading] = useState(false);
  const [claudeApiKey, setClaudeApiKey] = useState('');
  const [showApiKeySettings, setShowApiKeySettings] = useState(false);
  const [encounters, setEncounters] = useState<Encounter[]>([]);
  const [generatedEncounter, setGeneratedEncounter] = useState<Encounter | null>(null);
  const [encounterLoading, setEncounterLoading] = useState(false);

  const addTrigger = () => {
    if (newTrigger.trim()) {
      setTriggers([...triggers, { id: Date.now().toString(), text: newTrigger.trim() }]);
      setNewTrigger('');
    }
  };

  const removeTrigger = (id: string) => {
    setTriggers(triggers.filter(trigger => trigger.id !== id));
  };

  const addResponse = () => {
    if (newResponse.trim()) {
      setResponses([...responses, { id: Date.now().toString(), text: newResponse.trim() }]);
      setNewResponse('');
    }
  };

  const removeResponse = (id: string) => {
    setResponses(responses.filter(response => response.id !== id));
  };

  const addBlindspot = () => {
    setBlindspots([...blindspots, {
      id: Date.now().toString(),
      description: '',
      selfJustification: '',
      canCrack: false,
    }]);
  };

  const removeBlindspot = (id: string) => {
    setBlindspots(blindspots.filter(blindspot => blindspot.id !== id));
  };

  const updateBlindspot = (id: string, field: keyof Blindspot, value: string | boolean) => {
    setBlindspots(blindspots.map(blindspot =>
      blindspot.id === id ? { ...blindspot, [field]: value } : blindspot
    ));
  };

  const addMutation = () => {
    setMutations([...mutations, {
      id: Date.now().toString(),
      name: '',
      description: '',
      probability: 0,
    }]);
  };

  const removeMutation = (id: string) => {
    setMutations(mutations.filter(mutation => mutation.id !== id));
  };

  const updateMutation = (id: string, field: keyof Mutation, value: string | number) => {
    setMutations(mutations.map(mutation =>
      mutation.id === id ? { ...mutation, [field]: value } : mutation
    ));
  };

  const handleKeyPress = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  // Load enzymes from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-enzymes');
    if (stored) {
      try {
        setSavedEnzymes(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to load enzymes from localStorage', e);
      }
    }
  }, []);

  // Load characters from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-characters');
    if (stored) {
      try {
        setCharacters(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to load characters from localStorage', e);
      }
    }
  }, []);

  // Save enzymes to localStorage whenever they change
  useEffect(() => {
    if (savedEnzymes.length > 0 || localStorage.getItem('splinewright-enzymes')) {
      localStorage.setItem('splinewright-enzymes', JSON.stringify(savedEnzymes));
    }
  }, [savedEnzymes]);

  // Save characters to localStorage whenever they change
  useEffect(() => {
    if (characters.length > 0 || localStorage.getItem('splinewright-characters')) {
      localStorage.setItem('splinewright-characters', JSON.stringify(characters));
    }
  }, [characters]);

  // Load tension codons from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-tension-codons');
    if (stored) {
      try {
        setTensionCodons(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to load tension codons from localStorage', e);
      }
    }
  }, []);

  // Save tension codons to localStorage whenever they change
  useEffect(() => {
    if (tensionCodons.length > 0 || localStorage.getItem('splinewright-tension-codons')) {
      localStorage.setItem('splinewright-tension-codons', JSON.stringify(tensionCodons));
    }
  }, [tensionCodons]);

  // Load escalation profile from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-escalation-profile');
    if (stored) {
      try {
        setEscalationProfile(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to load escalation profile from localStorage', e);
      }
    }
  }, []);

  // Save escalation profile to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('splinewright-escalation-profile', JSON.stringify(escalationProfile));
  }, [escalationProfile]);

  // Load encounters from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-encounters');
    if (stored) {
      try {
        setEncounters(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to load encounters from localStorage', e);
      }
    }
  }, []);

  // Load Claude API key from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-claude-api-key');
    if (stored) {
      setClaudeApiKey(stored);
    }
  }, []);

  // Save Claude API key to localStorage whenever it changes
  useEffect(() => {
    if (claudeApiKey) {
      localStorage.setItem('splinewright-claude-api-key', claudeApiKey);
    }
  }, [claudeApiKey]);

  // Save encounters to localStorage whenever they change
  useEffect(() => {
    if (encounters.length > 0 || localStorage.getItem('splinewright-encounters')) {
      localStorage.setItem('splinewright-encounters', JSON.stringify(encounters));
    }
  }, [encounters]);

  const saveEnzyme = () => {
    if (!characterName.trim() || !traitName.trim()) {
      alert('Please enter both character name and trait name before saving.');
      return;
    }

    const enzyme: Enzyme = {
      id: Date.now().toString(),
      characterName: characterName.trim(),
      traitName: traitName.trim(),
      intensity,
      triggers: [...triggers],
      responses: [...responses],
      blindspots: [...blindspots],
      mutations: [...mutations],
      createdAt: Date.now(),
    };

    setSavedEnzymes([...savedEnzymes, enzyme]);
    setSelectedEnzymeId(enzyme.id);
  };

  const loadEnzyme = (enzyme: Enzyme) => {
    setCharacterName(enzyme.characterName);
    setTraitName(enzyme.traitName);
    setIntensity(enzyme.intensity);
    setTriggers([...enzyme.triggers]);
    setResponses([...enzyme.responses]);
    setBlindspots([...(enzyme.blindspots || [])]);
    setMutations([...(enzyme.mutations || [])]);
    setSelectedEnzymeId(enzyme.id);
  };

  const newEnzyme = () => {
    setCharacterName('');
    setTraitName('');
    setIntensity(0.5);
    setTriggers([]);
    setResponses([]);
    setBlindspots([]);
    setMutations([]);
    setNewTrigger('');
    setNewResponse('');
    setSelectedEnzymeId(null);
  };

  const deleteEnzyme = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this enzyme?')) {
      setSavedEnzymes(savedEnzymes.filter(enzyme => enzyme.id !== id));
      if (selectedEnzymeId === id) {
        newEnzyme();
      }
      // Remove enzyme from all characters that have it attached
      setCharacters(characters.map(character => ({
        ...character,
        attachedEnzymes: character.attachedEnzymes.filter(ae => ae.enzymeId !== id)
      })));
    }
  };

  const createCharacter = () => {
    if (!newCharacterName.trim()) {
      alert('Please enter a character name.');
      return;
    }

    const character: Character = {
      id: Date.now().toString(),
      name: newCharacterName.trim(),
      description: newCharacterDescription.trim(),
      attachedEnzymes: [],
      createdAt: Date.now(),
    };

    setCharacters([...characters, character]);
    setNewCharacterName('');
    setNewCharacterDescription('');
    setSelectedCharacterId(character.id);
  };

  const deleteCharacter = (id: string) => {
    if (window.confirm('Are you sure you want to delete this character?')) {
      setCharacters(characters.filter(character => character.id !== id));
      if (selectedCharacterId === id) {
        setSelectedCharacterId(null);
      }
    }
  };

  const attachEnzymeToCharacter = (characterId: string, enzymeId: string) => {
    const character = characters.find(c => c.id === characterId);
    if (!character) return;

    // Check if enzyme is already attached
    if (character.attachedEnzymes.some(ae => ae.enzymeId === enzymeId)) {
      return;
    }

    const enzyme = savedEnzymes.find(e => e.id === enzymeId);
    if (!enzyme) return;

    const updatedCharacter: Character = {
      ...character,
      attachedEnzymes: [
        ...character.attachedEnzymes,
        { enzymeId, intensity: enzyme.intensity }
      ]
    };

    setCharacters(characters.map(c => c.id === characterId ? updatedCharacter : c));
  };

  const detachEnzymeFromCharacter = (characterId: string, enzymeId: string) => {
    setCharacters(characters.map(character =>
      character.id === characterId
        ? {
            ...character,
            attachedEnzymes: character.attachedEnzymes.filter(ae => ae.enzymeId !== enzymeId)
          }
        : character
    ));
  };

  const updateAttachedEnzymeIntensity = (characterId: string, enzymeId: string, intensity: number) => {
    setCharacters(characters.map(character =>
      character.id === characterId
        ? {
            ...character,
            attachedEnzymes: character.attachedEnzymes.map(ae =>
              ae.enzymeId === enzymeId ? { ...ae, intensity } : ae
            )
          }
        : character
    ));
  };

  const addTensionCodon = () => {
    const codon: TensionCodon = {
      id: Date.now().toString(),
      name: '',
      description: '',
      pressureLevel: 0.5,
      affectedCharacterIds: [],
      createdAt: Date.now(),
    };
    setTensionCodons([...tensionCodons, codon]);
  };

  const removeTensionCodon = (id: string) => {
    if (window.confirm('Are you sure you want to delete this tension codon?')) {
      setTensionCodons(tensionCodons.filter(codon => codon.id !== id));
    }
  };

  const updateTensionCodon = (id: string, field: keyof TensionCodon, value: string | number | string[]) => {
    setTensionCodons(tensionCodons.map(codon =>
      codon.id === id ? { ...codon, [field]: value } : codon
    ));
  };

  const toggleCharacterInTensionCodon = (codonId: string, characterId: string) => {
    const codon = tensionCodons.find(c => c.id === codonId);
    if (!codon) return;

    const isAffected = codon.affectedCharacterIds.includes(characterId);
    const newAffectedIds = isAffected
      ? codon.affectedCharacterIds.filter(id => id !== characterId)
      : [...codon.affectedCharacterIds, characterId];

    updateTensionCodon(codonId, 'affectedCharacterIds', newAffectedIds);
  };

  const updateEscalationProfile = (field: keyof EscalationProfile, value: EscalationType | number) => {
    setEscalationProfile(prev => {
      const updated = { ...prev, [field]: value };
      // When type changes, ensure we have default values for the new type
      if (field === 'type') {
        if (value === 'slow-burn' && !updated.slowBurnReinforcements) {
          updated.slowBurnReinforcements = 3;
        } else if (value === 'hair-trigger' && !updated.hairTriggerRecoveryTime) {
          updated.hairTriggerRecoveryTime = 1;
        } else if (value === 'wave') {
          if (!updated.waveBuildDuration) updated.waveBuildDuration = 5;
          if (!updated.waveReleaseIntensity) updated.waveReleaseIntensity = 0.7;
        }
      }
      return updated;
    });
  };

  const exportData = () => {
    const exportData: ExportData = {
      version: '1.0.0',
      timestamp: new Date().toISOString(),
      enzymes: savedEnzymes,
      characters: characters,
      tensionCodons: tensionCodons,
      escalationProfile: escalationProfile,
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `splinewright-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const importData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const data: ExportData = JSON.parse(text);

        // Validate the data structure
        if (!data.version || !data.enzymes || !data.characters || !data.tensionCodons || !data.escalationProfile) {
          alert('Invalid file format. Please select a valid Splinewright export file.');
          return;
        }

        // Confirm before importing (this will overwrite current data)
        if (!window.confirm('Importing will replace all current data. Are you sure you want to continue?')) {
          event.target.value = '';
          return;
        }

        // Import the data
        setSavedEnzymes(data.enzymes || []);
        setCharacters(data.characters || []);
        setTensionCodons(data.tensionCodons || []);
        setEscalationProfile(data.escalationProfile || {
          type: 'slow-burn',
          slowBurnReinforcements: 3,
          hairTriggerRecoveryTime: 1,
          waveBuildDuration: 5,
          waveReleaseIntensity: 0.7,
        });

        // Clear current editor state
        newEnzyme();
        setSelectedCharacterId(null);
        setSelectedEnzymeId(null);

        alert(`Successfully imported data from ${data.timestamp || 'unknown date'}`);
      } catch (error) {
        console.error('Import error:', error);
        alert('Failed to import file. Please make sure it is a valid JSON file.');
      }
      // Reset the file input
      event.target.value = '';
    };
    reader.readAsText(file);
  };

  const generateSuggestions = async () => {
    if (!claudeApiKey) {
      alert('Please set your Claude API key in settings first.');
      setShowApiKeySettings(true);
      setCobotPanelOpen(true);
      return;
    }

    if (!characterName.trim() || !traitName.trim()) {
      alert('Please enter a character name and trait name before generating suggestions.');
      return;
    }

    setCobotPanelOpen(true);
    setCobotLoading(true);
    setCobotSuggestions({ triggers: [], responses: [] });

    try {
      const prompt = `You are helping create character behavioral patterns called "enzymes" for an interactive storytelling tool.

Current Enzyme:
- Character: ${characterName}
- Trait: ${traitName}
- Intensity: ${intensity.toFixed(2)}
- Current Triggers: ${triggers.length > 0 ? triggers.map(t => t.text).join(', ') : 'None'}
- Current Responses: ${responses.length > 0 ? responses.map(r => r.text).join(', ') : 'None'}
${blindspots.length > 0 ? `- Blindspots: ${blindspots.map(b => b.description).join('; ')}` : ''}

Please suggest 3-5 additional triggers and 3-5 additional responses that would create dramatic opportunities for this character. 

Format your response as JSON with this exact structure:
{
  "triggers": ["trigger 1", "trigger 2", "trigger 3"],
  "responses": ["response 1", "response 2", "response 3"]
}

Make the suggestions specific, dramatic, and aligned with the character's trait and intensity level.`;

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': claudeApiKey,
          'anthropic-version': '2023-06-01',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          messages: [
            {
              role: 'user',
              content: prompt,
            },
          ],
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to generate suggestions');
      }

      const data = await response.json();
      const content = data.content[0].text;

      // Try to extract JSON from the response
      let suggestions: { triggers: string[]; responses: string[] } = { triggers: [], responses: [] };
      
      // Look for JSON in the response
      const jsonMatch = content.match(/\{[\s\S]*"triggers"[\s\S]*"responses"[\s\S]*\}/);
      if (jsonMatch) {
        try {
          suggestions = JSON.parse(jsonMatch[0]);
        } catch (e) {
          console.error('Failed to parse JSON from Claude response', e);
        }
      }

      // Fallback: try to extract suggestions from text if JSON parsing fails
      if (suggestions.triggers.length === 0 && suggestions.responses.length === 0) {
        const triggerMatches = content.match(/"triggers"\s*:\s*\[([\s\S]*?)\]/);
        const responseMatches = content.match(/"responses"\s*:\s*\[([\s\S]*?)\]/);
        
        if (triggerMatches) {
          const triggerStrings: string[] = triggerMatches[1].split(',');
          suggestions.triggers = triggerStrings
            .map((t: string) => t.trim().replace(/^["']|["']$/g, ''))
            .filter((t: string) => t.length > 0);
        }
        
        if (responseMatches) {
          const responseStrings: string[] = responseMatches[1].split(',');
          suggestions.responses = responseStrings
            .map((r: string) => r.trim().replace(/^["']|["']$/g, ''))
            .filter((r: string) => r.length > 0);
        }
      }

      setCobotSuggestions(suggestions);
    } catch (error: any) {
      console.error('Error generating suggestions:', error);
      alert(`Failed to generate suggestions: ${error.message || 'Unknown error'}`);
      setCobotSuggestions({ triggers: [], responses: [] });
    } finally {
      setCobotLoading(false);
    }
  };

  const addSuggestionTrigger = (triggerText: string) => {
    if (triggerText.trim()) {
      setTriggers([...triggers, { id: Date.now().toString(), text: triggerText.trim() }]);
    }
  };

  const addSuggestionResponse = (responseText: string) => {
    if (responseText.trim()) {
      setResponses([...responses, { id: Date.now().toString(), text: responseText.trim() }]);
    }
  };

  const generateEncounter = async () => {
    if (!claudeApiKey) {
      alert('Please set your Claude API key in Cobot settings (⚙️) first.');
      return;
    }

    if (characters.length < 2) {
      alert('You need at least 2 characters to generate an encounter.');
      return;
    }

    setEncounterLoading(true);
    setGeneratedEncounter(null);

    try {
      // Build character data with their enzymes
      const characterData = characters.map(char => {
        const attachedEnzymeData = char.attachedEnzymes.map(ae => {
          const enzyme = savedEnzymes.find(e => e.id === ae.enzymeId);
          if (!enzyme) return null;
          return {
            trait: enzyme.traitName,
            intensity: ae.intensity,
            triggers: enzyme.triggers.map(t => t.text),
            responses: enzyme.responses.map(r => r.text),
            blindspots: enzyme.blindspots.map(b => b.description),
          };
        }).filter(Boolean);

        return {
          name: char.name,
          description: char.description,
          enzymes: attachedEnzymeData,
        };
      });

      // Build tension codon data
      const tensionData = tensionCodons.map(codon => ({
        name: codon.name,
        description: codon.description,
        pressureLevel: codon.pressureLevel,
        affectedCharacters: characters
          .filter(c => codon.affectedCharacterIds.includes(c.id))
          .map(c => c.name),
      }));

      const prompt = `You are helping create dramatic encounters for an interactive storytelling tool.

CHARACTERS:
${characterData.map((char, i) => `
${i + 1}. ${char.name}
   Description: ${char.description || 'No description'}
   Enzymes (behavioral patterns):
${char.enzymes.map((enz: any) => `     - ${enz.trait} (intensity: ${enz.intensity.toFixed(2)})
       Triggers: ${enz.triggers.join(', ') || 'None'}
       Responses: ${enz.responses.join(', ') || 'None'}
       Blindspots: ${enz.blindspots.join('; ') || 'None'}`).join('\n') || '     No enzymes attached'}
`).join('\n')}

${tensionData.length > 0 ? `ACTIVE TENSIONS:
${tensionData.map(t => `- ${t.name} (pressure: ${t.pressureLevel.toFixed(2)}): ${t.description}
  Affects: ${t.affectedCharacters.join(', ')}`).join('\n')}
` : ''}

Generate a dramatic encounter between two of these characters. The encounter should:
1. Be triggered by their enzymes (triggers activating responses)
2. Create dramatic tension and conflict
3. Include player choice points where decisions matter
4. Leverage character blindspots and active tensions

Format your response as JSON with this exact structure:
{
  "title": "Encounter title",
  "characters": ["Character 1 name", "Character 2 name"],
  "triggers": "What triggers this encounter (what activates the characters' enzymes)",
  "whatHappens": "A detailed description of what happens in the encounter",
  "playerChoicePoints": [
    "Choice point 1 description",
    "Choice point 2 description",
    "Choice point 3 description"
  ]
}

Make it dramatic, specific, and leverage the character traits and tensions.`;

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': claudeApiKey,
          'anthropic-version': '2023-06-01',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 2000,
          messages: [
            {
              role: 'user',
              content: prompt,
            },
          ],
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to generate encounter');
      }

      const data = await response.json();
      const content = data.content[0].text;

      // Try to extract JSON from the response
      let encounterData: any = null;
      const jsonMatch = content.match(/\{[\s\S]*"title"[\s\S]*\}/);
      if (jsonMatch) {
        try {
          encounterData = JSON.parse(jsonMatch[0]);
        } catch (e) {
          console.error('Failed to parse JSON from Claude response', e);
        }
      }

      if (!encounterData || !encounterData.title || !encounterData.characters) {
        throw new Error('Invalid response format from Claude');
      }

      // Find character IDs from names
      const characterIds = encounterData.characters
        .map((name: string) => characters.find(c => c.name === name)?.id)
        .filter(Boolean);

      if (characterIds.length < 2) {
        throw new Error('Could not match character names to saved characters');
      }

      const encounter: Encounter = {
        id: Date.now().toString(),
        title: encounterData.title,
        description: encounterData.whatHappens || encounterData.description || '',
        characterIds: characterIds,
        triggers: encounterData.triggers || '',
        whatHappens: encounterData.whatHappens || '',
        playerChoicePoints: Array.isArray(encounterData.playerChoicePoints) 
          ? encounterData.playerChoicePoints 
          : [],
        createdAt: Date.now(),
      };

      setGeneratedEncounter(encounter);
    } catch (error: any) {
      console.error('Error generating encounter:', error);
      alert(`Failed to generate encounter: ${error.message || 'Unknown error'}`);
    } finally {
      setEncounterLoading(false);
    }
  };

  const saveGeneratedEncounter = () => {
    if (!generatedEncounter) return;
    setEncounters([...encounters, generatedEncounter]);
    setGeneratedEncounter(null);
  };

  const deleteEncounter = (id: string) => {
    if (window.confirm('Are you sure you want to delete this encounter?')) {
      setEncounters(encounters.filter(encounter => encounter.id !== id));
    }
  };

  const appContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#1a1a1a',
    color: '#e5e5e5',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  };

  const mainContentContainerStyle: React.CSSProperties = {
    display: 'flex',
    flex: 1,
    minHeight: 0,
  };

  const sidebarStyle: React.CSSProperties = {
    width: '300px',
    backgroundColor: '#222222',
    borderRight: '1px solid #3a3a3a',
    padding: '24px',
    overflowY: 'auto',
    flexShrink: 0,
  };

  const sidebarHeaderStyle: React.CSSProperties = {
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: '1px solid #3a3a3a',
  };

  const sidebarTitleStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: '600',
    margin: '0 0 8px 0',
    color: '#f5f5f5',
  };

  const enzymeListStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  };

  const enzymeItemStyle = (isSelected: boolean, isHovered: boolean): React.CSSProperties => ({
    padding: '12px',
    backgroundColor: isSelected ? '#2a4a6a' : isHovered ? '#2a2a2a' : '#252525',
    border: isSelected ? '1px solid #64b5f6' : '1px solid #3a3a3a',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    position: 'relative',
  });

  const enzymeItemHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '6px',
  };

  const enzymeNameStyle: React.CSSProperties = {
    fontWeight: '600',
    color: '#f5f5f5',
    fontSize: '0.95rem',
    flex: 1,
  };

  const enzymeDeleteButtonStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: '#ff6666',
    cursor: 'pointer',
    fontSize: '1.2rem',
    padding: '0 4px',
    lineHeight: '1',
    opacity: 0.7,
    transition: 'opacity 0.2s',
  };

  const enzymeTraitStyle: React.CSSProperties = {
    fontSize: '0.85rem',
    color: '#a0a0a0',
    marginBottom: '4px',
  };

  const enzymeMetaStyle: React.CSSProperties = {
    fontSize: '0.75rem',
    color: '#666',
    display: 'flex',
    gap: '8px',
  };

  const containerStyle: React.CSSProperties = {
    flex: 1,
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '60px 30px',
    overflowY: 'auto',
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: '50px',
    textAlign: 'center',
    paddingBottom: '30px',
    borderBottom: '1px solid #3a3a3a',
  };

  const h1Style: React.CSSProperties = {
    fontSize: '3rem',
    fontWeight: '600',
    margin: '0 0 12px 0',
    color: '#f5f5f5',
    letterSpacing: '-0.5px',
  };

  const formGroupStyle: React.CSSProperties = {
    marginBottom: '32px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '12px',
    fontWeight: '500',
    color: '#d0d0d0',
    fontSize: '1rem',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px',
    border: '1px solid #4a4a4a',
    borderRadius: '8px',
    fontSize: '1rem',
    boxSizing: 'border-box',
    backgroundColor: '#2a2a2a',
    color: '#f5f5f5',
    transition: 'border-color 0.2s, background-color 0.2s',
  };

  const sliderContainerStyle: React.CSSProperties = {
    marginTop: '16px',
    position: 'relative',
  };

  const sliderWrapperStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  };

  const sliderStyle: React.CSSProperties = {
    flex: 1,
    height: '8px',
    borderRadius: '4px',
    outline: 'none',
    backgroundColor: '#3a3a3a',
    WebkitAppearance: 'none',
    appearance: 'none',
  };

  const intensityValueStyle: React.CSSProperties = {
    display: 'inline-block',
    minWidth: '60px',
    padding: '8px 12px',
    backgroundColor: '#2a2a2a',
    borderRadius: '6px',
    fontWeight: '600',
    color: '#64b5f6',
    fontSize: '1.1rem',
    textAlign: 'center',
    border: '1px solid #4a4a4a',
  };

  const sectionsContainerStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '32px',
    marginTop: '48px',
  };

  const sectionStyle: React.CSSProperties = {
    border: '1px solid #3a3a3a',
    borderRadius: '12px',
    padding: '28px',
    backgroundColor: '#222222',
  };

  const sectionTitleStyle: React.CSSProperties = {
    margin: '0 0 10px 0',
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#f5f5f5',
  };

  const sectionDescStyle: React.CSSProperties = {
    margin: '0 0 24px 0',
    fontSize: '0.95rem',
    color: '#a0a0a0',
    lineHeight: '1.5',
  };

  const addContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
  };

  const getButtonStyle = (isHovered: boolean): React.CSSProperties => ({
    padding: '12px 24px',
    backgroundColor: isHovered ? '#4a9eff' : '#3d8bfd',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background-color 0.2s, transform 0.1s, box-shadow 0.2s',
    transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
    boxShadow: isHovered ? '0 4px 12px rgba(61, 139, 253, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.2)',
  });

  const itemsListStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxHeight: '500px',
    overflowY: 'auto',
    paddingRight: '4px',
  };

  const itemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    backgroundColor: '#2a2a2a',
    border: '1px solid #3a3a3a',
    borderRadius: '8px',
    transition: 'border-color 0.2s, background-color 0.2s',
  };

  const itemTextStyle: React.CSSProperties = {
    flex: 1,
    color: '#e5e5e5',
    fontSize: '0.95rem',
  };

  const getRemoveButtonStyle = (isHovered: boolean): React.CSSProperties => ({
    background: isHovered ? '#ff4444' : 'transparent',
    border: 'none',
    color: isHovered ? 'white' : '#ff6666',
    fontSize: '1.5rem',
    cursor: 'pointer',
    padding: '4px 10px',
    lineHeight: '1',
    borderRadius: '4px',
    transition: 'background-color 0.2s, color 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '32px',
    height: '32px',
  });

  const emptyStateStyle: React.CSSProperties = {
    textAlign: 'center',
    color: '#666',
    fontStyle: 'italic',
    padding: '40px 20px',
    fontSize: '0.95rem',
  };

  const actionButtonsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    marginTop: '32px',
    marginBottom: '32px',
  };

  const getNewButtonStyle = (isHovered: boolean): React.CSSProperties => ({
    padding: '12px 24px',
    backgroundColor: isHovered ? '#4a4a4a' : '#3a3a3a',
    color: 'white',
    border: '1px solid #4a4a4a',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background-color 0.2s, transform 0.1s, box-shadow 0.2s',
    transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
    boxShadow: isHovered ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.2)',
  });

  const emptySidebarStyle: React.CSSProperties = {
    textAlign: 'center',
    color: '#666',
    fontStyle: 'italic',
    padding: '40px 20px',
    fontSize: '0.9rem',
  };

  const blindspotItemStyle: React.CSSProperties = {
    padding: '20px',
    backgroundColor: '#2a2a2a',
    border: '1px solid #3a3a3a',
    borderRadius: '8px',
    marginBottom: '16px',
  };

  const blindspotFieldStyle: React.CSSProperties = {
    marginBottom: '16px',
  };

  const blindspotLabelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    color: '#d0d0d0',
    fontSize: '0.9rem',
  };

  const blindspotTextareaStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #4a4a4a',
    borderRadius: '6px',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    backgroundColor: '#2a2a2a',
    color: '#f5f5f5',
    transition: 'border-color 0.2s, background-color 0.2s',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '60px',
  };

  const blindspotCheckboxContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '12px',
  };

  const blindspotCheckboxStyle: React.CSSProperties = {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#64b5f6',
  };

  const blindspotCheckboxLabelStyle: React.CSSProperties = {
    color: '#d0d0d0',
    fontSize: '0.9rem',
    cursor: 'pointer',
  };

  const blindspotHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  };

  const blindspotTitleStyle: React.CSSProperties = {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#f5f5f5',
    margin: 0,
  };

  const mutationItemStyle: React.CSSProperties = {
    padding: '20px',
    backgroundColor: '#2a2a2a',
    border: '1px solid #3a3a3a',
    borderRadius: '8px',
    marginBottom: '16px',
  };

  const mutationFieldStyle: React.CSSProperties = {
    marginBottom: '16px',
  };

  const mutationLabelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    color: '#d0d0d0',
    fontSize: '0.9rem',
  };

  const mutationInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #4a4a4a',
    borderRadius: '6px',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    backgroundColor: '#2a2a2a',
    color: '#f5f5f5',
    transition: 'border-color 0.2s, background-color 0.2s',
    fontFamily: 'inherit',
  };

  const mutationTextareaStyle: React.CSSProperties = {
    ...mutationInputStyle,
    resize: 'vertical',
    minHeight: '80px',
  };

  const mutationHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  };

  const mutationTitleStyle: React.CSSProperties = {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#f5f5f5',
    margin: 0,
  };

  const mutationProbabilityContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  const mutationProbabilityInputStyle: React.CSSProperties = {
    width: '100px',
    padding: '8px 12px',
    border: '1px solid #4a4a4a',
    borderRadius: '6px',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    backgroundColor: '#2a2a2a',
    color: '#f5f5f5',
    transition: 'border-color 0.2s, background-color 0.2s',
    fontFamily: 'inherit',
  };

  const mutationProbabilityLabelStyle: React.CSSProperties = {
    color: '#d0d0d0',
    fontSize: '0.9rem',
  };

  // Tab navigation styles
  const tabContainerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #3a3a3a',
    backgroundColor: '#1a1a1a',
    padding: '0',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  };

  const tabsGroupStyle: React.CSSProperties = {
    display: 'flex',
  };

  const exportImportGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
    padding: '8px 16px',
  };

  const exportImportButtonStyle: React.CSSProperties = {
    padding: '8px 16px',
    backgroundColor: '#3a3a3a',
    color: 'white',
    border: '1px solid #4a4a4a',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s, transform 0.1s',
  };

  // Cobot panel styles
  const cobotPanelStyle: React.CSSProperties = {
    position: 'fixed',
    right: cobotPanelOpen ? '0' : '-400px',
    top: '60px',
    width: '380px',
    height: 'calc(100vh - 60px)',
    backgroundColor: '#222222',
    borderLeft: '1px solid #3a3a3a',
    boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.3)',
    transition: 'right 0.3s ease',
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
  };

  const cobotHeaderStyle: React.CSSProperties = {
    padding: '20px',
    borderBottom: '1px solid #3a3a3a',
    backgroundColor: '#1a1a1a',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const cobotTitleStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#f5f5f5',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  };

  const cobotCloseButtonStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: '#a0a0a0',
    fontSize: '1.5rem',
    cursor: 'pointer',
    padding: '0',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'background-color 0.2s, color 0.2s',
  };

  const cobotContentStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
  };

  const cobotEmptyStateStyle: React.CSSProperties = {
    textAlign: 'center',
    color: '#666',
    padding: '60px 20px',
    fontSize: '0.95rem',
    lineHeight: '1.6',
  };

  const cobotSuggestionItemStyle: React.CSSProperties = {
    padding: '16px',
    backgroundColor: '#2a2a2a',
    border: '1px solid #3a3a3a',
    borderRadius: '8px',
    marginBottom: '12px',
    color: '#e5e5e5',
    fontSize: '0.95rem',
    lineHeight: '1.6',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
  };

  const cobotSuggestionSectionStyle: React.CSSProperties = {
    marginBottom: '24px',
  };

  const cobotSuggestionSectionTitleStyle: React.CSSProperties = {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#f5f5f5',
    marginBottom: '12px',
  };

  const cobotApiKeyInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #4a4a4a',
    borderRadius: '6px',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    backgroundColor: '#2a2a2a',
    color: '#f5f5f5',
    fontFamily: 'monospace',
    marginBottom: '12px',
  };

  const cobotSettingsButtonStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: '#a0a0a0',
    fontSize: '1rem',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'background-color 0.2s, color 0.2s',
  };

  const cobotGenerateButtonStyle: React.CSSProperties = {
    padding: '10px 20px',
    backgroundColor: '#64b5f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s, transform 0.1s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  // Encounter styles
  const encounterItemStyle: React.CSSProperties = {
    padding: '20px',
    backgroundColor: '#222222',
    border: '1px solid #3a3a3a',
    borderRadius: '12px',
    marginBottom: '16px',
  };

  const encounterHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  };

  const encounterTitleStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#f5f5f5',
    margin: 0,
    flex: 1,
  };

  const encounterDescriptionStyle: React.CSSProperties = {
    fontSize: '0.95rem',
    color: '#d0d0d0',
    lineHeight: '1.6',
    marginBottom: '12px',
  };

  const encounterCharactersStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '12px',
  };

  const encounterCharacterTagStyle: React.CSSProperties = {
    padding: '6px 12px',
    backgroundColor: '#2a2a2a',
    border: '1px solid #4a4a4a',
    borderRadius: '6px',
    fontSize: '0.85rem',
    color: '#e5e5e5',
  };

  const tabStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '16px 24px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: isActive ? '2px solid #64b5f6' : '2px solid transparent',
    color: isActive ? '#f5f5f5' : '#a0a0a0',
    fontSize: '1rem',
    fontWeight: isActive ? '600' : '400',
    cursor: 'pointer',
    transition: 'all 0.2s',
  });

  // Character view styles
  const characterItemStyle = (isSelected: boolean, isHovered: boolean): React.CSSProperties => ({
    padding: '16px',
    backgroundColor: isSelected ? '#2a4a6a' : isHovered ? '#2a2a2a' : '#252525',
    border: isSelected ? '1px solid #64b5f6' : '1px solid #3a3a3a',
    borderRadius: '8px',
    marginBottom: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  });

  const characterNameStyle: React.CSSProperties = {
    fontWeight: '600',
    color: '#f5f5f5',
    fontSize: '1.1rem',
    marginBottom: '8px',
  };

  const characterDescriptionStyle: React.CSSProperties = {
    fontSize: '0.9rem',
    color: '#a0a0a0',
    marginBottom: '12px',
    lineHeight: '1.5',
  };

  const attachedEnzymeItemStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    backgroundColor: '#2a2a2a',
    border: '1px solid #3a3a3a',
    borderRadius: '6px',
    marginBottom: '8px',
  };

  const attachedEnzymeInfoStyle: React.CSSProperties = {
    flex: 1,
  };

  const attachedEnzymeNameStyle: React.CSSProperties = {
    fontWeight: '500',
    color: '#f5f5f5',
    fontSize: '0.95rem',
    marginBottom: '4px',
  };

  const attachedEnzymeTraitStyle: React.CSSProperties = {
    fontSize: '0.85rem',
    color: '#a0a0a0',
  };

  const attachedEnzymeIntensityStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginRight: '8px',
  };

  const intensitySliderSmallStyle: React.CSSProperties = {
    width: '100px',
    height: '4px',
    borderRadius: '2px',
    outline: 'none',
    backgroundColor: '#3a3a3a',
    WebkitAppearance: 'none',
    appearance: 'none',
  };

  const intensityValueSmallStyle: React.CSSProperties = {
    minWidth: '45px',
    textAlign: 'right',
    fontSize: '0.9rem',
    color: '#64b5f6',
    fontWeight: '600',
  };

  const enzymeSelectStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #4a4a4a',
    borderRadius: '6px',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    backgroundColor: '#2a2a2a',
    color: '#f5f5f5',
    transition: 'border-color 0.2s, background-color 0.2s',
    fontFamily: 'inherit',
  };

  // Tension Codon styles
  const tensionCodonItemStyle: React.CSSProperties = {
    padding: '24px',
    backgroundColor: '#222222',
    border: '1px solid #3a3a3a',
    borderRadius: '12px',
    marginBottom: '24px',
  };

  const tensionCodonHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  };

  const tensionCodonTitleStyle: React.CSSProperties = {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#f5f5f5',
    margin: 0,
  };

  const tensionCodonFieldStyle: React.CSSProperties = {
    marginBottom: '20px',
  };

  const tensionCodonLabelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    color: '#d0d0d0',
    fontSize: '0.95rem',
  };

  const tensionCodonTextareaStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #4a4a4a',
    borderRadius: '6px',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    backgroundColor: '#2a2a2a',
    color: '#f5f5f5',
    transition: 'border-color 0.2s, background-color 0.2s',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '80px',
  };

  const tensionCodonPressureContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginTop: '12px',
  };

  const tensionCodonPressureSliderStyle: React.CSSProperties = {
    flex: 1,
    height: '8px',
    borderRadius: '4px',
    outline: 'none',
    backgroundColor: '#3a3a3a',
    WebkitAppearance: 'none',
    appearance: 'none',
  };

  const tensionCodonPressureValueStyle: React.CSSProperties = {
    minWidth: '60px',
    padding: '8px 12px',
    backgroundColor: '#2a2a2a',
    borderRadius: '6px',
    fontWeight: '600',
    color: '#64b5f6',
    fontSize: '1.1rem',
    textAlign: 'center',
    border: '1px solid #4a4a4a',
  };

  const affectedCharactersContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '12px',
  };

  const characterCheckboxItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    backgroundColor: '#2a2a2a',
    border: '1px solid #3a3a3a',
    borderRadius: '6px',
  };

  const characterCheckboxStyle: React.CSSProperties = {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#64b5f6',
  };

  const characterCheckboxLabelStyle: React.CSSProperties = {
    color: '#e5e5e5',
    fontSize: '0.95rem',
    cursor: 'pointer',
    flex: 1,
  };

  return (
    <>
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #64b5f6;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(100, 181, 246, 0.4);
          transition: all 0.2s;
        }
        input[type="range"]::-webkit-slider-thumb:hover {
          background: #90caf9;
          transform: scale(1.1);
          box-shadow: 0 3px 8px rgba(100, 181, 246, 0.6);
        }
        input[type="range"]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #64b5f6;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 6px rgba(100, 181, 246, 0.4);
          transition: all 0.2s;
        }
        input[type="range"]::-moz-range-thumb:hover {
          background: #90caf9;
          transform: scale(1.1);
          box-shadow: 0 3px 8px rgba(100, 181, 246, 0.6);
        }
        input[type="text"]:focus,
        input[type="number"]:focus,
        textarea:focus {
          outline: none;
          border-color: #64b5f6;
          background-color: #2f2f2f;
        }
        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #1a1a1a;
        }
        ::-webkit-scrollbar-thumb {
          background: #4a4a4a;
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #5a5a5a;
        }
        select:focus {
          outline: none;
          border-color: #64b5f6;
          background-color: #2f2f2f;
        }
      `}</style>
      <div style={appContainerStyle}>
        <div style={tabContainerStyle}>
          <div style={tabsGroupStyle}>
            <button
              style={tabStyle(currentView === 'editor')}
              onClick={() => setCurrentView('editor')}
            >
              Enzyme Editor
            </button>
            <button
              style={tabStyle(currentView === 'characters')}
              onClick={() => setCurrentView('characters')}
            >
              Characters
            </button>
            <button
              style={tabStyle(currentView === 'storyworld')}
              onClick={() => setCurrentView('storyworld')}
            >
              Storyworld
            </button>
            <button
              style={tabStyle(currentView === 'encounters')}
              onClick={() => setCurrentView('encounters')}
            >
              Encounters
            </button>
          </div>
          <div style={exportImportGroupStyle}>
            <input
              type="file"
              accept=".json"
              onChange={importData}
              style={{ display: 'none' }}
              id="import-file-input"
            />
            <button
              onClick={() => document.getElementById('import-file-input')?.click()}
              style={exportImportButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#4a4a4a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3a3a3a';
              }}
            >
              Import
            </button>
            <button
              onClick={exportData}
              style={exportImportButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#4a4a4a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3a3a3a';
              }}
            >
              Export
            </button>
          </div>
        </div>
        <div style={mainContentContainerStyle}>
          <div style={sidebarStyle}>
            {currentView === 'editor' ? (
              <>
                <div style={sidebarHeaderStyle}>
                  <h2 style={sidebarTitleStyle}>Saved Enzymes</h2>
                  <p style={{ margin: 0, color: '#a0a0a0', fontSize: '0.85rem' }}>
                    {savedEnzymes.length} {savedEnzymes.length === 1 ? 'enzyme' : 'enzymes'}
                  </p>
                </div>
                <div style={enzymeListStyle}>
            {savedEnzymes.length === 0 ? (
              <p style={emptySidebarStyle}>No enzymes saved yet</p>
            ) : (
              savedEnzymes.map((enzyme) => (
                <div
                  key={enzyme.id}
                  style={enzymeItemStyle(selectedEnzymeId === enzyme.id, enzymeHover === enzyme.id)}
                  onClick={() => loadEnzyme(enzyme)}
                  onMouseEnter={() => setEnzymeHover(enzyme.id)}
                  onMouseLeave={() => setEnzymeHover(null)}
                >
                  <div style={enzymeItemHeaderStyle}>
                    <div style={enzymeNameStyle}>{enzyme.characterName}</div>
                    <button
                      onClick={(e) => deleteEnzyme(enzyme.id, e)}
                      style={enzymeDeleteButtonStyle}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '0.7';
                      }}
                      aria-label="Delete enzyme"
                    >
                      ×
                    </button>
                  </div>
                  <div style={enzymeTraitStyle}>{enzyme.traitName}</div>
                  <div style={enzymeMetaStyle}>
                    <span>Intensity: {enzyme.intensity.toFixed(2)}</span>
                    <span>•</span>
                    <span>{enzyme.triggers.length} triggers</span>
                    <span>•</span>
                    <span>{enzyme.responses.length} responses</span>
                    <span>•</span>
                    <span>{(enzyme.blindspots || []).length} blindspots</span>
                    <span>•</span>
                    <span>{(enzyme.mutations || []).length} mutations</span>
                  </div>
                </div>
              ))
            )}
                </div>
              </>
            ) : currentView === 'characters' ? (
              <>
                <div style={sidebarHeaderStyle}>
                  <h2 style={sidebarTitleStyle}>Characters</h2>
                  <p style={{ margin: 0, color: '#a0a0a0', fontSize: '0.85rem' }}>
                    {characters.length} {characters.length === 1 ? 'character' : 'characters'}
                  </p>
                </div>
                <div style={enzymeListStyle}>
                  {characters.length === 0 ? (
                    <p style={emptySidebarStyle}>No characters created yet</p>
                  ) : (
                    characters.map((character) => (
                      <div
                        key={character.id}
                        style={characterItemStyle(selectedCharacterId === character.id, characterHover === character.id)}
                        onClick={() => setSelectedCharacterId(character.id)}
                        onMouseEnter={() => setCharacterHover(character.id)}
                        onMouseLeave={() => setCharacterHover(null)}
                      >
                        <div style={characterNameStyle}>{character.name}</div>
                        {character.description && (
                          <div style={characterDescriptionStyle}>
                            {character.description.length > 60
                              ? character.description.substring(0, 60) + '...'
                              : character.description}
                          </div>
                        )}
                        <div style={enzymeMetaStyle}>
                          <span>{character.attachedEnzymes.length} enzymes</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : currentView === 'storyworld' ? (
              <>
                <div style={sidebarHeaderStyle}>
                  <h2 style={sidebarTitleStyle}>Tension Codons</h2>
                  <p style={{ margin: 0, color: '#a0a0a0', fontSize: '0.85rem' }}>
                    {tensionCodons.length} {tensionCodons.length === 1 ? 'codon' : 'codons'}
                  </p>
                </div>
                <div style={enzymeListStyle}>
                  {tensionCodons.length === 0 ? (
                    <p style={emptySidebarStyle}>No tension codons created yet</p>
                  ) : (
                    tensionCodons.map((codon) => (
                      <div
                        key={codon.id}
                        style={enzymeItemStyle(false, tensionCodonHover === codon.id)}
                        onMouseEnter={() => setTensionCodonHover(codon.id)}
                        onMouseLeave={() => setTensionCodonHover(null)}
                      >
                        <div style={enzymeItemHeaderStyle}>
                          <div style={enzymeNameStyle}>{codon.name || 'Unnamed Codon'}</div>
                        </div>
                        <div style={enzymeTraitStyle}>Pressure: {codon.pressureLevel.toFixed(2)}</div>
                        <div style={enzymeMetaStyle}>
                          <span>{codon.affectedCharacterIds.length} characters</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <>
                <div style={sidebarHeaderStyle}>
                  <h2 style={sidebarTitleStyle}>Encounters</h2>
                  <p style={{ margin: 0, color: '#a0a0a0', fontSize: '0.85rem' }}>
                    {encounters.length} {encounters.length === 1 ? 'encounter' : 'encounters'}
                  </p>
                </div>
                <div style={enzymeListStyle}>
                  {encounters.length === 0 ? (
                    <p style={emptySidebarStyle}>No encounters saved yet</p>
                  ) : (
                    encounters.map((encounter) => (
                      <div
                        key={encounter.id}
                        style={enzymeItemStyle(false, false)}
                      >
                        <div style={enzymeItemHeaderStyle}>
                          <div style={enzymeNameStyle}>{encounter.title || 'Untitled Encounter'}</div>
                        </div>
                        <div style={enzymeTraitStyle}>
                          {encounter.characterIds.length} {encounter.characterIds.length === 1 ? 'character' : 'characters'}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

        <div style={containerStyle}>
          {currentView === 'editor' ? (
            <>
              <header style={headerStyle}>
                <h1 style={h1Style}>Splinewright</h1>
                <p style={{ margin: 0, color: '#a0a0a0', fontSize: '1.1rem' }}>Enzyme authoring system</p>
              </header>

          <div style={actionButtonsStyle}>
            <button
              onClick={newEnzyme}
              style={getNewButtonStyle(buttonHover === 'new')}
              onMouseEnter={() => setButtonHover('new')}
              onMouseLeave={() => setButtonHover(null)}
            >
              New Enzyme
            </button>
            <button
              onClick={saveEnzyme}
              style={getButtonStyle(buttonHover === 'save')}
              onMouseEnter={() => setButtonHover('save')}
              onMouseLeave={() => setButtonHover(null)}
            >
              Save Enzyme
            </button>
            <button
              onClick={generateSuggestions}
              style={cobotGenerateButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#90caf9';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#64b5f6';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <span>✨</span>
              Generate Suggestions
            </button>
          </div>

          <div>
          <div style={formGroupStyle}>
            <label style={labelStyle} htmlFor="character-name">
              Character Name
            </label>
            <input
              id="character-name"
              type="text"
              value={characterName}
              onChange={(e) => setCharacterName(e.target.value)}
              placeholder="Enter character name"
              style={inputStyle}
            />
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle} htmlFor="trait-name">
              Trait Name
            </label>
            <input
              id="trait-name"
              type="text"
              value={traitName}
              onChange={(e) => setTraitName(e.target.value)}
              placeholder="Enter trait name (e.g., 'curiosity', 'bravery')"
              style={inputStyle}
            />
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle} htmlFor="intensity">
              Intensity
            </label>
            <div style={sliderContainerStyle}>
              <div style={sliderWrapperStyle}>
                <input
                  id="intensity"
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={intensity}
                  onChange={(e) => setIntensity(parseFloat(e.target.value))}
                  style={sliderStyle}
                />
                <span style={intensityValueStyle}>{intensity.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        <div style={sectionsContainerStyle}>
          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Triggers</h2>
            <p style={sectionDescStyle}>Things that activate this behavior</p>
            <div style={addContainerStyle}>
              <input
                type="text"
                value={newTrigger}
                onChange={(e) => setNewTrigger(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, addTrigger)}
                placeholder="Enter a trigger..."
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                onClick={addTrigger}
                style={getButtonStyle(buttonHover === 'add-trigger')}
                onMouseEnter={() => setButtonHover('add-trigger')}
                onMouseLeave={() => setButtonHover(null)}
              >
                Add
              </button>
            </div>
            <div style={itemsListStyle}>
              {triggers.length === 0 ? (
                <p style={emptyStateStyle}>No triggers added yet</p>
              ) : (
                triggers.map((trigger) => (
                  <div key={trigger.id} style={itemStyle}>
                    <span style={itemTextStyle}>{trigger.text}</span>
                    <button
                      onClick={() => removeTrigger(trigger.id)}
                      style={getRemoveButtonStyle(removeHover === trigger.id)}
                      onMouseEnter={() => setRemoveHover(trigger.id)}
                      onMouseLeave={() => setRemoveHover(null)}
                      aria-label="Remove trigger"
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Responses</h2>
            <p style={sectionDescStyle}>What the character does when triggered</p>
            <div style={addContainerStyle}>
              <input
                type="text"
                value={newResponse}
                onChange={(e) => setNewResponse(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, addResponse)}
                placeholder="Enter a response..."
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                onClick={addResponse}
                style={getButtonStyle(buttonHover === 'add-response')}
                onMouseEnter={() => setButtonHover('add-response')}
                onMouseLeave={() => setButtonHover(null)}
              >
                Add
              </button>
            </div>
            <div style={itemsListStyle}>
              {responses.length === 0 ? (
                <p style={emptyStateStyle}>No responses added yet</p>
              ) : (
                responses.map((response) => (
                  <div key={response.id} style={itemStyle}>
                    <span style={itemTextStyle}>{response.text}</span>
                    <button
                      onClick={() => removeResponse(response.id)}
                      style={getRemoveButtonStyle(removeHover === response.id)}
                      onMouseEnter={() => setRemoveHover(response.id)}
                      onMouseLeave={() => setRemoveHover(null)}
                      aria-label="Remove response"
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div style={{ marginTop: '48px' }}>
          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Blindspots</h2>
            <p style={sectionDescStyle}>Things the character cannot see about themselves</p>
            <div style={{ marginBottom: '24px' }}>
              <button
                onClick={addBlindspot}
                style={getButtonStyle(buttonHover === 'add-blindspot')}
                onMouseEnter={() => setButtonHover('add-blindspot')}
                onMouseLeave={() => setButtonHover(null)}
              >
                Add Blindspot
              </button>
            </div>
            <div>
              {blindspots.length === 0 ? (
                <p style={emptyStateStyle}>No blindspots added yet</p>
              ) : (
                blindspots.map((blindspot) => (
                  <div key={blindspot.id} style={blindspotItemStyle}>
                    <div style={blindspotHeaderStyle}>
                      <h3 style={blindspotTitleStyle}>Blindspot {blindspots.indexOf(blindspot) + 1}</h3>
                      <button
                        onClick={() => removeBlindspot(blindspot.id)}
                        style={getRemoveButtonStyle(removeHover === blindspot.id)}
                        onMouseEnter={() => setRemoveHover(blindspot.id)}
                        onMouseLeave={() => setRemoveHover(null)}
                        aria-label="Remove blindspot"
                      >
                        ×
                      </button>
                    </div>
                    <div style={blindspotFieldStyle}>
                      <label style={blindspotLabelStyle} htmlFor={`blindspot-desc-${blindspot.id}`}>
                        Description
                      </label>
                      <textarea
                        id={`blindspot-desc-${blindspot.id}`}
                        value={blindspot.description}
                        onChange={(e) => updateBlindspot(blindspot.id, 'description', e.target.value)}
                        placeholder="What the character cannot see about themselves..."
                        style={blindspotTextareaStyle}
                      />
                    </div>
                    <div style={blindspotFieldStyle}>
                      <label style={blindspotLabelStyle} htmlFor={`blindspot-just-${blindspot.id}`}>
                        Self-Justification
                      </label>
                      <textarea
                        id={`blindspot-just-${blindspot.id}`}
                        value={blindspot.selfJustification}
                        onChange={(e) => updateBlindspot(blindspot.id, 'selfJustification', e.target.value)}
                        placeholder="How the character explains their behavior to themselves..."
                        style={blindspotTextareaStyle}
                      />
                    </div>
                    <div style={blindspotCheckboxContainerStyle}>
                      <input
                        type="checkbox"
                        id={`blindspot-crack-${blindspot.id}`}
                        checked={blindspot.canCrack}
                        onChange={(e) => updateBlindspot(blindspot.id, 'canCrack', e.target.checked)}
                        style={blindspotCheckboxStyle}
                      />
                      <label
                        htmlFor={`blindspot-crack-${blindspot.id}`}
                        style={blindspotCheckboxLabelStyle}
                      >
                        Can crack under pressure
                      </label>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div style={{ marginTop: '48px' }}>
          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Mutations</h2>
            <p style={sectionDescStyle}>How the enzyme changes under sustained pressure</p>
            <div style={{ marginBottom: '24px' }}>
              <button
                onClick={addMutation}
                style={getButtonStyle(buttonHover === 'add-mutation')}
                onMouseEnter={() => setButtonHover('add-mutation')}
                onMouseLeave={() => setButtonHover(null)}
              >
                Add Mutation Path
              </button>
            </div>
            <div>
              {mutations.length === 0 ? (
                <p style={emptyStateStyle}>No mutations added yet</p>
              ) : (
                mutations.map((mutation) => (
                  <div key={mutation.id} style={mutationItemStyle}>
                    <div style={mutationHeaderStyle}>
                      <h3 style={mutationTitleStyle}>Mutation Path {mutations.indexOf(mutation) + 1}</h3>
                      <button
                        onClick={() => removeMutation(mutation.id)}
                        style={getRemoveButtonStyle(removeHover === mutation.id)}
                        onMouseEnter={() => setRemoveHover(mutation.id)}
                        onMouseLeave={() => setRemoveHover(null)}
                        aria-label="Remove mutation"
                      >
                        ×
                      </button>
                    </div>
                    <div style={mutationFieldStyle}>
                      <label style={mutationLabelStyle} htmlFor={`mutation-name-${mutation.id}`}>
                        Name
                      </label>
                      <input
                        id={`mutation-name-${mutation.id}`}
                        type="text"
                        value={mutation.name}
                        onChange={(e) => updateMutation(mutation.id, 'name', e.target.value)}
                        placeholder="e.g., 'Hardening', 'Collapse'"
                        style={mutationInputStyle}
                      />
                    </div>
                    <div style={mutationFieldStyle}>
                      <label style={mutationLabelStyle} htmlFor={`mutation-desc-${mutation.id}`}>
                        Description
                      </label>
                      <textarea
                        id={`mutation-desc-${mutation.id}`}
                        value={mutation.description}
                        onChange={(e) => updateMutation(mutation.id, 'description', e.target.value)}
                        placeholder="What the enzyme becomes under sustained pressure..."
                        style={mutationTextareaStyle}
                      />
                    </div>
                    <div style={mutationFieldStyle}>
                      <label style={mutationLabelStyle} htmlFor={`mutation-prob-${mutation.id}`}>
                        Probability
                      </label>
                      <div style={mutationProbabilityContainerStyle}>
                        <input
                          id={`mutation-prob-${mutation.id}`}
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={mutation.probability}
                          onChange={(e) => updateMutation(mutation.id, 'probability', Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
                          style={mutationProbabilityInputStyle}
                        />
                        <span style={mutationProbabilityLabelStyle}>%</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
            </>
          ) : currentView === 'characters' ? (
            <>
              <header style={headerStyle}>
                <h1 style={h1Style}>Characters</h1>
                <p style={{ margin: 0, color: '#a0a0a0', fontSize: '1.1rem' }}>Manage characters and their enzymes</p>
              </header>

              {selectedCharacterId ? (
                <div>
                  {(() => {
                    const character = characters.find(c => c.id === selectedCharacterId);
                    if (!character) return null;
                    return (
                      <div>
                        <div style={{ marginBottom: '32px' }}>
                          <button
                            onClick={() => setSelectedCharacterId(null)}
                            style={getNewButtonStyle(buttonHover === 'back')}
                            onMouseEnter={() => setButtonHover('back')}
                            onMouseLeave={() => setButtonHover(null)}
                          >
                            ← Back to Characters
                          </button>
                        </div>

                        <div style={sectionStyle}>
                          <h2 style={sectionTitleStyle}>{character.name}</h2>
                          {character.description && (
                            <p style={sectionDescStyle}>{character.description}</p>
                          )}

                          <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle} htmlFor="attach-enzyme">
                              Attach Enzyme
                            </label>
                            <select
                              id="attach-enzyme"
                              onChange={(e) => {
                                if (e.target.value) {
                                  attachEnzymeToCharacter(character.id, e.target.value);
                                  e.target.value = '';
                                }
                              }}
                              style={enzymeSelectStyle}
                            >
                              <option value="">Select an enzyme to attach...</option>
                              {savedEnzymes
                                .filter(enzyme => !character.attachedEnzymes.some(ae => ae.enzymeId === enzyme.id))
                                .map(enzyme => (
                                  <option key={enzyme.id} value={enzyme.id}>
                                    {enzyme.characterName} - {enzyme.traitName}
                                  </option>
                                ))}
                            </select>
                          </div>

                          <div>
                            <h3 style={{ ...sectionTitleStyle, fontSize: '1.25rem', marginBottom: '16px' }}>
                              Attached Enzymes
                            </h3>
                            {character.attachedEnzymes.length === 0 ? (
                              <p style={emptyStateStyle}>No enzymes attached yet</p>
                            ) : (
                              character.attachedEnzymes.map((attachedEnzyme) => {
                                const enzyme = savedEnzymes.find(e => e.id === attachedEnzyme.enzymeId);
                                if (!enzyme) return null;
                                return (
                                  <div key={attachedEnzyme.enzymeId} style={attachedEnzymeItemStyle}>
                                    <div style={attachedEnzymeInfoStyle}>
                                      <div style={attachedEnzymeNameStyle}>{enzyme.characterName}</div>
                                      <div style={attachedEnzymeTraitStyle}>{enzyme.traitName}</div>
                                    </div>
                                    <div style={attachedEnzymeIntensityStyle}>
                                      <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.01"
                                        value={attachedEnzyme.intensity}
                                        onChange={(e) => updateAttachedEnzymeIntensity(character.id, attachedEnzyme.enzymeId, parseFloat(e.target.value))}
                                        style={intensitySliderSmallStyle}
                                      />
                                      <span style={intensityValueSmallStyle}>{attachedEnzyme.intensity.toFixed(2)}</span>
                                      <button
                                        onClick={() => detachEnzymeFromCharacter(character.id, attachedEnzyme.enzymeId)}
                                        style={getRemoveButtonStyle(removeHover === attachedEnzyme.enzymeId)}
                                        onMouseEnter={() => setRemoveHover(attachedEnzyme.enzymeId)}
                                        onMouseLeave={() => setRemoveHover(null)}
                                        aria-label="Detach enzyme"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: '32px' }}>
                    <div style={sectionStyle}>
                      <h2 style={sectionTitleStyle}>Create New Character</h2>
                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="new-character-name">
                          Character Name
                        </label>
                        <input
                          id="new-character-name"
                          type="text"
                          value={newCharacterName}
                          onChange={(e) => setNewCharacterName(e.target.value)}
                          placeholder="Enter character name"
                          style={inputStyle}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              createCharacter();
                            }
                          }}
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="new-character-description">
                          Description
                        </label>
                        <textarea
                          id="new-character-description"
                          value={newCharacterDescription}
                          onChange={(e) => setNewCharacterDescription(e.target.value)}
                          placeholder="Enter character description (optional)"
                          style={{ ...blindspotTextareaStyle, minHeight: '100px' }}
                        />
                      </div>
                      <button
                        onClick={createCharacter}
                        style={getButtonStyle(buttonHover === 'create-character')}
                        onMouseEnter={() => setButtonHover('create-character')}
                        onMouseLeave={() => setButtonHover(null)}
                      >
                        Create Character
                      </button>
                    </div>
                  </div>

                  <div>
                    <h2 style={sectionTitleStyle}>All Characters</h2>
                    {characters.length === 0 ? (
                      <p style={emptyStateStyle}>No characters created yet. Create one above!</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {characters.map((character) => (
                          <div key={character.id} style={sectionStyle}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                              <div>
                                <h3 style={{ ...sectionTitleStyle, fontSize: '1.25rem', margin: 0 }}>
                                  {character.name}
                                </h3>
                                {character.description && (
                                  <p style={sectionDescStyle}>{character.description}</p>
                                )}
                              </div>
                              <button
                                onClick={() => deleteCharacter(character.id)}
                                style={getRemoveButtonStyle(removeHover === character.id)}
                                onMouseEnter={() => setRemoveHover(character.id)}
                                onMouseLeave={() => setRemoveHover(null)}
                                aria-label="Delete character"
                              >
                                ×
                              </button>
                            </div>
                            <div>
                              <h4 style={{ ...labelStyle, marginBottom: '12px' }}>Attached Enzymes</h4>
                              {character.attachedEnzymes.length === 0 ? (
                                <p style={{ ...emptyStateStyle, padding: '20px', fontSize: '0.9rem' }}>
                                  No enzymes attached
                                </p>
                              ) : (
                                character.attachedEnzymes.map((attachedEnzyme) => {
                                  const enzyme = savedEnzymes.find(e => e.id === attachedEnzyme.enzymeId);
                                  if (!enzyme) return null;
                                  return (
                                    <div key={attachedEnzyme.enzymeId} style={attachedEnzymeItemStyle}>
                                      <div style={attachedEnzymeInfoStyle}>
                                        <div style={attachedEnzymeNameStyle}>{enzyme.characterName}</div>
                                        <div style={attachedEnzymeTraitStyle}>{enzyme.traitName}</div>
                                      </div>
                                      <div style={attachedEnzymeIntensityStyle}>
                                        <span style={intensityValueSmallStyle}>Intensity: {attachedEnzyme.intensity.toFixed(2)}</span>
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                            <div style={{ marginTop: '16px' }}>
                              <button
                                onClick={() => setSelectedCharacterId(character.id)}
                                style={getButtonStyle(buttonHover === `edit-${character.id}`)}
                                onMouseEnter={() => setButtonHover(`edit-${character.id}`)}
                                onMouseLeave={() => setButtonHover(null)}
                              >
                                Edit Character
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : currentView === 'storyworld' ? (
            <>
              <header style={headerStyle}>
                <h1 style={h1Style}>Storyworld</h1>
                <p style={{ margin: 0, color: '#a0a0a0', fontSize: '1.1rem' }}>Global dramatic settings and tensions</p>
              </header>

              <div style={{ marginTop: '48px' }}>
                <div style={sectionStyle}>
                  <h2 style={sectionTitleStyle}>Escalation Profile</h2>
                  <p style={sectionDescStyle}>Controls the dramatic pacing of the story</p>

                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="escalation-type">
                      Escalation Type
                    </label>
                    <select
                      id="escalation-type"
                      value={escalationProfile.type}
                      onChange={(e) => updateEscalationProfile('type', e.target.value as EscalationType)}
                      style={enzymeSelectStyle}
                    >
                      <option value="slow-burn">Slow-burn</option>
                      <option value="hair-trigger">Hair-trigger</option>
                      <option value="wave">Wave</option>
                    </select>
                  </div>

                  {escalationProfile.type === 'slow-burn' && (
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="slow-burn-reinforcements">
                        Reinforcements Needed Before Eruption
                      </label>
                      <input
                        id="slow-burn-reinforcements"
                        type="number"
                        min="1"
                        step="1"
                        value={escalationProfile.slowBurnReinforcements || 3}
                        onChange={(e) => updateEscalationProfile('slowBurnReinforcements', parseInt(e.target.value) || 1)}
                        style={inputStyle}
                      />
                    </div>
                  )}

                  {escalationProfile.type === 'hair-trigger' && (
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="hair-trigger-recovery">
                        Recovery Time Between Eruptions
                      </label>
                      <input
                        id="hair-trigger-recovery"
                        type="number"
                        min="0"
                        step="0.1"
                        value={escalationProfile.hairTriggerRecoveryTime || 1}
                        onChange={(e) => updateEscalationProfile('hairTriggerRecoveryTime', parseFloat(e.target.value) || 0)}
                        style={inputStyle}
                      />
                    </div>
                  )}

                  {escalationProfile.type === 'wave' && (
                    <>
                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="wave-build-duration">
                          Build Duration
                        </label>
                        <input
                          id="wave-build-duration"
                          type="number"
                          min="1"
                          step="1"
                          value={escalationProfile.waveBuildDuration || 5}
                          onChange={(e) => updateEscalationProfile('waveBuildDuration', parseInt(e.target.value) || 1)}
                          style={inputStyle}
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="wave-release-intensity">
                          Release Intensity
                        </label>
                        <div style={sliderContainerStyle}>
                          <div style={sliderWrapperStyle}>
                            <input
                              id="wave-release-intensity"
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={escalationProfile.waveReleaseIntensity || 0.7}
                              onChange={(e) => updateEscalationProfile('waveReleaseIntensity', parseFloat(e.target.value))}
                              style={sliderStyle}
                            />
                            <span style={intensityValueStyle}>
                              {(escalationProfile.waveReleaseIntensity || 0.7).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div style={{ ...sectionStyle, marginTop: '32px' }}>
                  <h2 style={sectionTitleStyle}>Tension Codons</h2>
                  <p style={sectionDescStyle}>Abstract dramatic pressures active across the whole story</p>
                  <div style={{ marginBottom: '24px' }}>
                    <button
                      onClick={addTensionCodon}
                      style={getButtonStyle(buttonHover === 'add-tension-codon')}
                      onMouseEnter={() => setButtonHover('add-tension-codon')}
                      onMouseLeave={() => setButtonHover(null)}
                    >
                      Add Tension Codon
                    </button>
                  </div>
                  <div>
                    {tensionCodons.length === 0 ? (
                      <p style={emptyStateStyle}>No tension codons added yet</p>
                    ) : (
                      tensionCodons.map((codon) => (
                        <div key={codon.id} style={tensionCodonItemStyle}>
                          <div style={tensionCodonHeaderStyle}>
                            <h3 style={tensionCodonTitleStyle}>
                              {codon.name || 'Unnamed Tension Codon'}
                            </h3>
                            <button
                              onClick={() => removeTensionCodon(codon.id)}
                              style={getRemoveButtonStyle(removeHover === codon.id)}
                              onMouseEnter={() => setRemoveHover(codon.id)}
                              onMouseLeave={() => setRemoveHover(null)}
                              aria-label="Remove tension codon"
                            >
                              ×
                            </button>
                          </div>

                          <div style={tensionCodonFieldStyle}>
                            <label style={tensionCodonLabelStyle} htmlFor={`codon-name-${codon.id}`}>
                              Name
                            </label>
                            <input
                              id={`codon-name-${codon.id}`}
                              type="text"
                              value={codon.name}
                              onChange={(e) => updateTensionCodon(codon.id, 'name', e.target.value)}
                              placeholder="e.g., 'Concealed Identity', 'Forbidden Desire'"
                              style={inputStyle}
                            />
                          </div>

                          <div style={tensionCodonFieldStyle}>
                            <label style={tensionCodonLabelStyle} htmlFor={`codon-desc-${codon.id}`}>
                              Description
                            </label>
                            <textarea
                              id={`codon-desc-${codon.id}`}
                              value={codon.description}
                              onChange={(e) => updateTensionCodon(codon.id, 'description', e.target.value)}
                              placeholder="Describe this dramatic pressure..."
                              style={tensionCodonTextareaStyle}
                            />
                          </div>

                          <div style={tensionCodonFieldStyle}>
                            <label style={tensionCodonLabelStyle} htmlFor={`codon-pressure-${codon.id}`}>
                              Pressure Level
                            </label>
                            <div style={tensionCodonPressureContainerStyle}>
                              <input
                                id={`codon-pressure-${codon.id}`}
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={codon.pressureLevel}
                                onChange={(e) => updateTensionCodon(codon.id, 'pressureLevel', parseFloat(e.target.value))}
                                style={tensionCodonPressureSliderStyle}
                              />
                              <span style={tensionCodonPressureValueStyle}>
                                {codon.pressureLevel.toFixed(2)}
                              </span>
                            </div>
                          </div>

                          <div style={tensionCodonFieldStyle}>
                            <label style={tensionCodonLabelStyle}>
                              Affected Characters
                            </label>
                            {characters.length === 0 ? (
                              <p style={{ ...emptyStateStyle, padding: '20px', fontSize: '0.9rem' }}>
                                No characters created yet. Create characters first.
                              </p>
                            ) : (
                              <div style={affectedCharactersContainerStyle}>
                                {characters.map((character) => {
                                  const isAffected = codon.affectedCharacterIds.includes(character.id);
                                  return (
                                    <div key={character.id} style={characterCheckboxItemStyle}>
                                      <input
                                        type="checkbox"
                                        id={`codon-${codon.id}-char-${character.id}`}
                                        checked={isAffected}
                                        onChange={() => toggleCharacterInTensionCodon(codon.id, character.id)}
                                        style={characterCheckboxStyle}
                                      />
                                      <label
                                        htmlFor={`codon-${codon.id}-char-${character.id}`}
                                        style={characterCheckboxLabelStyle}
                                      >
                                        {character.name}
                                      </label>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <header style={headerStyle}>
                <h1 style={h1Style}>Encounters</h1>
                <p style={{ margin: 0, color: '#a0a0a0', fontSize: '1.1rem' }}>Generate and manage dramatic encounters</p>
              </header>

              <div style={{ marginTop: '48px' }}>
                <div style={sectionStyle}>
                  <h2 style={sectionTitleStyle}>Generate Encounter</h2>
                  <p style={sectionDescStyle}>Create dramatic encounters based on your characters, enzymes, and tension codons</p>
                  <div style={{ marginBottom: '32px' }}>
                    <button
                      onClick={generateEncounter}
                      disabled={encounterLoading}
                      style={{
                        ...getButtonStyle(buttonHover === 'generate-encounter'),
                        opacity: encounterLoading ? 0.6 : 1,
                        cursor: encounterLoading ? 'not-allowed' : 'pointer',
                      }}
                      onMouseEnter={() => !encounterLoading && setButtonHover('generate-encounter')}
                      onMouseLeave={() => setButtonHover(null)}
                    >
                      {encounterLoading ? 'Generating...' : 'Generate Encounter'}
                    </button>
                  </div>

                  {encounterLoading && (
                    <div style={cobotEmptyStateStyle}>
                      <div style={{ fontSize: '3rem', marginBottom: '20px' }}>⏳</div>
                      <div style={{ fontWeight: '600', color: '#a0a0a0', marginBottom: '12px' }}>
                        Generating Encounter...
                      </div>
                      <div>
                        Claude is creating a dramatic encounter based on your characters and tensions.
                      </div>
                    </div>
                  )}

                  {generatedEncounter && !encounterLoading && (
                    <div style={{ ...encounterItemStyle, borderColor: '#64b5f6', borderWidth: '2px' }}>
                      <div style={encounterHeaderStyle}>
                        <h3 style={encounterTitleStyle}>{generatedEncounter.title}</h3>
                      </div>
                      
                      {generatedEncounter.characterIds.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ ...labelStyle, marginBottom: '8px' }}>Characters Involved:</div>
                          <div style={encounterCharactersStyle}>
                            {characters
                              .filter(c => generatedEncounter.characterIds.includes(c.id))
                              .map((character) => (
                                <span key={character.id} style={encounterCharacterTagStyle}>
                                  {character.name}
                                </span>
                              ))}
                          </div>
                        </div>
                      )}

                      {generatedEncounter.triggers && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ ...labelStyle, marginBottom: '8px' }}>What Triggers It:</div>
                          <div style={encounterDescriptionStyle}>{generatedEncounter.triggers}</div>
                        </div>
                      )}

                      {generatedEncounter.whatHappens && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ ...labelStyle, marginBottom: '8px' }}>What Happens:</div>
                          <div style={encounterDescriptionStyle}>{generatedEncounter.whatHappens}</div>
                        </div>
                      )}

                      {generatedEncounter.playerChoicePoints && generatedEncounter.playerChoicePoints.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ ...labelStyle, marginBottom: '8px' }}>Player Choice Points:</div>
                          <ul style={{ margin: 0, paddingLeft: '20px', color: '#d0d0d0' }}>
                            {generatedEncounter.playerChoicePoints.map((choice, index) => (
                              <li key={index} style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                                {choice}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                        <button
                          onClick={saveGeneratedEncounter}
                          style={getButtonStyle(buttonHover === 'save-encounter')}
                          onMouseEnter={() => setButtonHover('save-encounter')}
                          onMouseLeave={() => setButtonHover(null)}
                        >
                          Save Encounter
                        </button>
                        <button
                          onClick={() => setGeneratedEncounter(null)}
                          style={getNewButtonStyle(buttonHover === 'discard-encounter')}
                          onMouseEnter={() => setButtonHover('discard-encounter')}
                          onMouseLeave={() => setButtonHover(null)}
                        >
                          Discard
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ ...sectionStyle, marginTop: '32px' }}>
                  <h2 style={sectionTitleStyle}>Saved Encounters</h2>
                  <p style={sectionDescStyle}>Your generated encounters</p>
                  <div>
                    {encounters.length === 0 ? (
                      <p style={emptyStateStyle}>No encounters saved yet. Generate your first encounter above!</p>
                    ) : (
                      encounters.map((encounter) => {
                        const involvedCharacters = characters.filter(c => encounter.characterIds.includes(c.id));
                        return (
                          <div key={encounter.id} style={encounterItemStyle}>
                            <div style={encounterHeaderStyle}>
                              <h3 style={encounterTitleStyle}>{encounter.title || 'Untitled Encounter'}</h3>
                              <button
                                onClick={() => deleteEncounter(encounter.id)}
                                style={getRemoveButtonStyle(removeHover === encounter.id)}
                                onMouseEnter={() => setRemoveHover(encounter.id)}
                                onMouseLeave={() => setRemoveHover(null)}
                                aria-label="Delete encounter"
                              >
                                ×
                              </button>
                            </div>
                            {involvedCharacters.length > 0 && (
                              <div style={{ marginBottom: '16px' }}>
                                <div style={{ ...labelStyle, marginBottom: '8px' }}>Characters Involved:</div>
                                <div style={encounterCharactersStyle}>
                                  {involvedCharacters.map((character) => (
                                    <span key={character.id} style={encounterCharacterTagStyle}>
                                      {character.name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {encounter.triggers && (
                              <div style={{ marginBottom: '16px' }}>
                                <div style={{ ...labelStyle, marginBottom: '8px' }}>What Triggers It:</div>
                                <div style={encounterDescriptionStyle}>{encounter.triggers}</div>
                              </div>
                            )}

                            {(encounter.whatHappens || encounter.description) && (
                              <div style={{ marginBottom: '16px' }}>
                                <div style={{ ...labelStyle, marginBottom: '8px' }}>What Happens:</div>
                                <div style={encounterDescriptionStyle}>{encounter.whatHappens || encounter.description}</div>
                              </div>
                            )}

                            {encounter.playerChoicePoints && encounter.playerChoicePoints.length > 0 && (
                              <div style={{ marginBottom: '16px' }}>
                                <div style={{ ...labelStyle, marginBottom: '8px' }}>Player Choice Points:</div>
                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#d0d0d0' }}>
                                  {encounter.playerChoicePoints.map((choice, index) => (
                                    <li key={index} style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                                      {choice}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        </div>
      </div>

      {/* Cobot Panel */}
      <div style={cobotPanelStyle}>
        <div style={cobotHeaderStyle}>
          <h2 style={cobotTitleStyle}>
            <span>🤖</span>
            Cobot
          </h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => setShowApiKeySettings(!showApiKeySettings)}
              style={cobotSettingsButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#3a3a3a';
                e.currentTarget.style.color = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#a0a0a0';
              }}
              aria-label="Settings"
            >
              ⚙️
            </button>
            <button
              onClick={() => setCobotPanelOpen(false)}
              style={cobotCloseButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#3a3a3a';
                e.currentTarget.style.color = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#a0a0a0';
              }}
              aria-label="Close Cobot panel"
            >
              ×
            </button>
          </div>
        </div>
        <div style={cobotContentStyle}>
          {showApiKeySettings ? (
            <div>
              <h3 style={{ ...cobotSuggestionSectionTitleStyle, marginBottom: '16px' }}>Claude API Settings</h3>
              <label style={labelStyle} htmlFor="claude-api-key">
                Claude API Key
              </label>
              <input
                id="claude-api-key"
                type="password"
                value={claudeApiKey}
                onChange={(e) => setClaudeApiKey(e.target.value)}
                placeholder="sk-ant-..."
                style={cobotApiKeyInputStyle}
              />
              <p style={{ fontSize: '0.85rem', color: '#a0a0a0', marginTop: '8px', lineHeight: '1.5' }}>
                Your API key is stored locally in your browser. Get your key from{' '}
                <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#64b5f6' }}>
                  Anthropic Console
                </a>
              </p>
              <button
                onClick={() => setShowApiKeySettings(false)}
                style={getButtonStyle(buttonHover === 'close-settings')}
                onMouseEnter={() => setButtonHover('close-settings')}
                onMouseLeave={() => setButtonHover(null)}
              >
                Done
              </button>
            </div>
          ) : cobotLoading ? (
            <div style={cobotEmptyStateStyle}>
              <div style={{ fontSize: '3rem', marginBottom: '20px' }}>⏳</div>
              <div style={{ fontWeight: '600', color: '#a0a0a0', marginBottom: '12px' }}>
                Generating Suggestions...
              </div>
              <div>
                Claude is analyzing your enzyme and generating dramatic suggestions.
              </div>
            </div>
          ) : cobotSuggestions.triggers.length === 0 && cobotSuggestions.responses.length === 0 ? (
            <div style={cobotEmptyStateStyle}>
              <div style={{ fontSize: '3rem', marginBottom: '20px' }}>✨</div>
              <div style={{ fontWeight: '600', color: '#a0a0a0', marginBottom: '12px' }}>
                AI Suggestions
              </div>
              <div>
                Click "Generate Suggestions" to get AI-powered recommendations for your enzyme.
                <br />
                <br />
                {!claudeApiKey && (
                  <>
                    <strong>Note:</strong> You'll need to set your Claude API key in settings (⚙️) first.
                  </>
                )}
              </div>
            </div>
          ) : (
            <div>
              {cobotSuggestions.triggers.length > 0 && (
                <div style={cobotSuggestionSectionStyle}>
                  <h3 style={cobotSuggestionSectionTitleStyle}>Suggested Triggers</h3>
                  {cobotSuggestions.triggers.map((trigger, index) => (
                    <div
                      key={index}
                      style={cobotSuggestionItemStyle}
                      onClick={() => addSuggestionTrigger(trigger)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#64b5f6';
                        e.currentTarget.style.backgroundColor = '#2f2f2f';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#3a3a3a';
                        e.currentTarget.style.backgroundColor = '#2a2a2a';
                      }}
                    >
                      {trigger}
                      <div style={{ fontSize: '0.8rem', color: '#64b5f6', marginTop: '8px' }}>
                        Click to add
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {cobotSuggestions.responses.length > 0 && (
                <div style={cobotSuggestionSectionStyle}>
                  <h3 style={cobotSuggestionSectionTitleStyle}>Suggested Responses</h3>
                  {cobotSuggestions.responses.map((response, index) => (
                    <div
                      key={index}
                      style={cobotSuggestionItemStyle}
                      onClick={() => addSuggestionResponse(response)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#64b5f6';
                        e.currentTarget.style.backgroundColor = '#2f2f2f';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#3a3a3a';
                        e.currentTarget.style.backgroundColor = '#2a2a2a';
                      }}
                    >
                      {response}
                      <div style={{ fontSize: '0.8rem', color: '#64b5f6', marginTop: '8px' }}>
                        Click to add
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default App;
